import type { Season } from '@/time/clock';

/**
 * Weather is derived, not stored.
 *
 * A save keeps only the world seed and the day; the forecast for any day is a
 * pure function of the two. That makes the weather identical across reloads
 * without persisting it, and — more usefully later — lets the game show
 * tomorrow's forecast on the TV without deciding it early.
 */

export const WEATHER_KINDS = ['clear', 'cloudy', 'rain', 'storm', 'snow'] as const;
export type Weather = (typeof WEATHER_KINDS)[number];

export const WEATHER_NAMES: Readonly<Record<Weather, string>> = {
  clear: 'Clear',
  cloudy: 'Cloudy',
  rain: 'Rain',
  storm: 'Storm',
  snow: 'Snow',
};

/**
 * Cumulative probability tables per season.
 *
 * Spring and autumn are wet, summer is mostly bright with the occasional
 * violent storm, winter trades rain for snow. Entries must end at 1.
 */
const TABLES: Readonly<Record<Season, readonly (readonly [Weather, number])[]>> = {
  spring: [
    ['clear', 0.45],
    ['cloudy', 0.7],
    ['rain', 0.95],
    ['storm', 1],
  ],
  summer: [
    ['clear', 0.6],
    ['cloudy', 0.8],
    ['rain', 0.92],
    ['storm', 1],
  ],
  autumn: [
    ['clear', 0.4],
    ['cloudy', 0.68],
    ['rain', 0.93],
    ['storm', 1],
  ],
  winter: [
    ['clear', 0.45],
    ['cloudy', 0.72],
    ['snow', 1],
  ],
};

/** The first few days are always calm, so a new game is never rained on. */
const GRACE_DAYS = 2;

export function weatherFor(seed: number, dayIndex: number, season: Season): Weather {
  if (dayIndex < GRACE_DAYS) return 'clear';

  const roll = hash01(seed, dayIndex);
  const table = TABLES[season];

  for (const [weather, threshold] of table) {
    if (roll < threshold) return weather;
  }
  return 'clear';
}

/** Does this weather put anything in the air outdoors? */
export function hasPrecipitation(weather: Weather): boolean {
  return weather === 'rain' || weather === 'storm' || weather === 'snow';
}

/** Extra darkening applied on top of the time-of-day ambient. */
export function weatherGloom(weather: Weather): number {
  switch (weather) {
    case 'cloudy':
      return 0.1;
    case 'rain':
      return 0.2;
    case 'storm':
      return 0.32;
    case 'snow':
      return 0.12;
    case 'clear':
      return 0;
  }
}

/**
 * Deterministic hash to the unit interval. Chosen over Math.random so the same
 * save always sees the same weather, and over a stateful PRNG so any day can be
 * queried without replaying the ones before it.
 */
function hash01(seed: number, dayIndex: number): number {
  let n = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) + Math.imul(dayIndex + 1, 0xc2b2ae35);
  n = (n ^ (n >>> 15)) >>> 0;
  n = Math.imul(n, 0x27d4eb2f) >>> 0;
  return ((n ^ (n >>> 13)) >>> 0) / 4294967296;
}
